import type {
    CreateRegistrationResponse,
    RegistrationApprovalResponse,
    RegistrationDetailResponse,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    deleteDoseDataset,
    deleteImageDataset,
} from '~/backend/connector-next-api/datasetDeleteClient';
import {
    deleteRegistration,
    getRegistrationDetail,
    patchRegistrationLabel,
    postRegistration,
    postRegistrationApproval,
    putRegistrationMatrix,
} from '~/backend/connector-next-api/registrationSaveClient';
import { PersistenceRequestError } from '~/domain/persistence/PersistenceErrors';
import type {
    ConnectorDatasetId,
    ConnectorPatientId,
    ConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';
import {
    asConnectorDatasetId,
    asConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';
import type {
    DatasetDeletionGateway,
    PersistedRegistrationDetail,
    RegistrationPersistenceGateway,
} from '~/domain/persistence/RegistrationPersistenceGateway';
import type { PersistedApprovalStatus } from '~/domain/persistence/StructureSetPersistenceGateway';

/**
 * Maps a wire DTO into the domain shape. A response without an id or
 * version is unusable for the save workflow's bookkeeping — treat it as a
 * failed request rather than proceeding with missing identity.
 */
function toDetail(
    dto:
        | CreateRegistrationResponse
        | RegistrationDetailResponse
        | RegistrationApprovalResponse
): PersistedRegistrationDetail {
    if (!dto.id || !('version' in dto) || !dto.version) {
        throw new PersistenceRequestError(null, null);
    }
    return {
        datasetId: asConnectorDatasetId(dto.id),
        studyInstanceUid:
            'studyInstanceUid' in dto ? (dto.studyInstanceUid ?? null) : null,
        seriesInstanceUid:
            'seriesInstanceUid' in dto ? (dto.seriesInstanceUid ?? null) : null,
        sopInstanceUid:
            'sopInstanceUid' in dto ? (dto.sopInstanceUid ?? null) : null,
        approvalStatus:
            'approvalStatus' in dto ? (dto.approvalStatus ?? null) : null,
        version: asConnectorVersionToken(dto.version),
    };
}

/** connector-next implementation of the registration persistence port. */
export class ConnectorRegistrationGateway
    implements RegistrationPersistenceGateway
{
    async create(request: {
        readonly patientId: ConnectorPatientId;
        readonly studyInstanceUid: string;
        readonly fixedFrameOfReferenceUid: string;
        readonly movingFrameOfReferenceUid: string;
        readonly matrixRowMajor: readonly number[];
        readonly label: string | null;
    }): Promise<PersistedRegistrationDetail> {
        return toDetail(
            await postRegistration({
                patientId: request.patientId,
                studyInstanceUid: request.studyInstanceUid,
                fixedFrameOfReferenceUid: request.fixedFrameOfReferenceUid,
                movingFrameOfReferenceUid: request.movingFrameOfReferenceUid,
                matrix: [...request.matrixRowMajor],
                label: request.label,
            })
        );
    }

    async updateMatrix(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        matrixRowMajor: readonly number[]
    ): Promise<PersistedRegistrationDetail> {
        return toDetail(
            await putRegistrationMatrix(id, ifMatch, matrixRowMajor)
        );
    }

    async setApproval(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        status: PersistedApprovalStatus,
        reviewerName: string | null
    ): Promise<PersistedRegistrationDetail> {
        return toDetail(
            await postRegistrationApproval(id, ifMatch, {
                status,
                reviewerName,
            })
        );
    }

    async updateLabel(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        label: string
    ): Promise<{ readonly version: ConnectorVersionToken }> {
        const response = await patchRegistrationLabel(id, ifMatch, label);
        if (!response.version) {
            // Without the post-update token the next If-Match would be
            // stale bookkeeping — treat as a failed request.
            throw new PersistenceRequestError(null, null);
        }
        return { version: asConnectorVersionToken(response.version) };
    }

    async deleteRegistration(id: ConnectorDatasetId): Promise<void> {
        await deleteRegistration(id);
    }

    async getDetail(
        id: ConnectorDatasetId
    ): Promise<PersistedRegistrationDetail> {
        return toDetail(await getRegistrationDetail(id));
    }
}

/** connector-next implementation of the image/dose deletion port. */
export class ConnectorDatasetDeletionGateway implements DatasetDeletionGateway {
    async deleteImage(id: ConnectorDatasetId): Promise<void> {
        await deleteImageDataset(id);
    }

    async deleteDose(id: ConnectorDatasetId): Promise<void> {
        await deleteDoseDataset(id);
    }
}
